import JsBarcode from 'jsbarcode';
import { hasWord, productLine } from 'app/main/apps/e-commerce/order/tabs/boxLabel';

/**
 * PGSS-5747: SKU barcode labels and find-by-scan. Agreed 18 Oct 2026, PGSS-5824 made them 6" x 4": printed on the label printer already
 * used for box labels, one label per page. The barcode is Code 128 of the SKU only (never the name or price), so a
 * printed label never goes out of date; the SKU and the product name are printed as text.
 */

/** Modules of white either side of the bars: Code 128 asks for at least 10. */
export const QUIET_ZONE = 10;

/** Printable ASCII only: Code 128 B can't encode anything else (an NBSP or "é" in legacy data). */
const ENCODABLE = /^[\x20-\x7E]+$/;

/** The Code 128 bar pattern of a SKU as "1" (bar) / "0" (space) modules, or '' when it can't be encoded. */
export function skuBars(sku) {
  const text = String(sku || '').trim();
  if (!text || !ENCODABLE.test(text)) return '';
  try {
    const target = {};
    JsBarcode(target, text, { format: 'CODE128' });
    return (target.encodings || []).map((e) => e.data).join('');
  } catch (e) {
    return '';
  }
}

/** Runs of bars as { x, w } in modules, offset by the quiet zone, for an SVG. */
export function barRects(bars) {
  const rects = [];
  let start = -1;
  for (let i = 0; i <= bars.length; i += 1) {
    if (bars[i] === '1' && start < 0) start = i;
    if (bars[i] !== '1' && start >= 0) {
      rects.push({ x: start + QUIET_ZONE, w: i - start });
      start = -1;
    }
  }
  return rects;
}

/** The products a label can be printed for: those with a SKU that encodes. */
export function labelProducts(products) {
  return (products || []).filter((p) => skuBars(p?.sku));
}

/** The label printer's resolution: thermal label printers print at 203 dots per inch. */
export const PRINTER_DPI = 203;
/** The widest the barcode may be on a 6in label (with its quiet zones). */
export const MAX_BARCODE_IN = 5.4;
/** The widest a module may be: past this a short SKU's bars span more than a handheld scanner reads in one sweep. */
export const MAX_DOTS_PER_MODULE = 4;

/**
 * Each bar module as a whole number of printer dots (review of #2747): stretching to a fixed width gave 2.9 or 1.5
 * dots a module, which the printer rounds unevenly. The barcode is the widest whole-dot size that fits; null when even
 * one dot a module doesn't fit (a SKU far over 30 characters).
 */
export function barcodeWidthIn(modules) {
  const dots = Math.min(MAX_DOTS_PER_MODULE, Math.floor((MAX_BARCODE_IN * PRINTER_DPI) / modules));
  return dots >= 1 ? (modules * dots) / PRINTER_DPI : null;
}

/** The first value that has text. A list row sends names, the product form sends picked options (or typed text). */
function fieldText(...values) {
  for (let i = 0; i < values.length; i += 1) {
    const value = values[i];
    const text = typeof value === 'object' ? value?.inputValue || value?.name : value;
    if (typeof text === 'string' && text.trim()) return text.trim();
  }
  return '';
}

/**
 * PGSS-5864: what the label says about the product: its full name, then any of its colour, type, size and finish the
 * name leaves out, and its shape. Each value prints once: most names already include them.
 */
export function skuProductText(product) {
  const name = fieldText(product?.name);
  const color = fieldText(product?.stone_color_name, product?.stone_color);
  const type = fieldText(product?.type_name, product?.type);
  const polish = fieldText(product?.polish, product?.finish);
  const shape = fieldText(product?.sub_type_name, product?.sub_type, product?.subType);
  const { length, width, height } = product || {};
  const line = productLine({ name, color, type, polish, length, width, height });
  const missing = name ? [color, type].filter((value) => value && !hasWord(name, value)) : [];
  const text = [...missing, line].filter(Boolean).join(' ');
  return { text, shape: shape && !hasWord(text, shape) ? shape : '' };
}

/**
 * What a scan found: open the one product (live before archived), or say none has that SKU. A scanner types the
 * SKU and presses Enter; the backend matches it exactly, ignoring capitals.
 */
export function scanResult(matches, sku) {
  const shown = String(sku || '').trim().toUpperCase();
  const list = matches || [];
  const live = list.filter((m) => !m.archived);
  if (!list.length) return { productId: null, message: `No product has SKU ${shown}` };
  // A duplicate SKU (possible until PGSS-5744's unique index is in): say so rather than open one of them at random.
  if (live.length > 1) {
    return { productId: null, message: `${live.length} products have SKU ${shown}. Fix the duplicate in Settings, SKU Codes.` };
  }
  return { productId: (live[0] || list[0]).id, message: null };
}

/**
 * Who sees Scan SKU: the roles that can open the product a scan finds (/products/:id is admin, staff and sales). Every
 * role a user has counts, whether `role` is one name or a list (review of #2747).
 */
export function canScanSku(user) {
  const names = [].concat(user?.role || [], (user?.roles || []).map((r) => (typeof r === 'string' ? r : r?.name)));
  return names.some((n) => ['admin', 'super admin', 'staff', 'sales'].includes(String(n || '').trim().toLowerCase()));
}
